import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Logo } from '../../components/Navbar';

const INDIA_BANK = [
  ['Account Name', 'M/S MS DISCOVERIES'],
  ['Account Number', '759601010050400'],
  ['Bank', 'UNION BANK OF INDIA'],
  ['Branch', 'PARAMATHI VELUR'],
  ['IFSC Code', 'UBIN0575968'],
  ['SWIFT Code', 'UBININBBERD']
];

const US_BANK = [
  ['Account Name', 'MS DISCOVERIES'],
  ['Account Number', '8336276214'],
  ['ACH Routing', '026073150'],
  ['Bank', 'Community Federal Savings Bank'],
  ['Address', '5 Penn Plaza, New York, NY 10001, US']
];

const PLAN_PRICES = {
  pack_one:      { usd_m: 99,  usd_q: 267, usd_y: 950,  inr_m: 5200,  inr_q: 14040, inr_y: 49920, name: 'Pack One' },
  pack_two:      { usd_m: 149, usd_q: 402, usd_y: 1430, inr_m: 7499,  inr_q: 20247, inr_y: 71990,  name: 'Pack Two' },
  pack_advanced: { usd_m: 299, usd_q: 807, usd_y: 2870, inr_m: 11999, inr_q: 32397, inr_y: 115190, name: 'Pack Advanced' }
};

export default function PaymentPage() {
  const [tab, setTab] = useState('india');
  const [txnId, setTxnId] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Get info from sessionStorage (set during registration or renewal)
  const email    = sessionStorage.getItem('pay_email') || '';
  const company  = sessionStorage.getItem('pay_company') || '';
  const planKey  = sessionStorage.getItem('pay_plan') || 'pack_two';
  const currency = (sessionStorage.getItem('pay_currency') || 'usd').toLowerCase();
  const originalCycle = sessionStorage.getItem('pay_cycle') || 'monthly';
  const bizId    = sessionStorage.getItem('pay_business_id') || '';
  const address  = sessionStorage.getItem('pay_address') || '';
  const gstin    = sessionStorage.getItem('pay_gstin') || '';
  const isRenewal = !!bizId || sessionStorage.getItem('pay_was_renewal') === '1'; // If bizId exists or was renewal

  // For renewal: allowed cycles based on current plan
  // monthly → monthly/quarterly/yearly
  // quarterly → quarterly/yearly
  // yearly → yearly only
  const getAllowedCycles = () => {
    if (originalCycle === 'yearly') return ['yearly'];
    if (originalCycle === 'quarterly') return ['quarterly', 'yearly'];
    return ['monthly', 'quarterly', 'yearly'];
  };
  const allowedCycles = getAllowedCycles();
  const [selectedCycle, setSelectedCycle] = useState(originalCycle);
  const cycle = selectedCycle; // always use selectedCycle — initialized from originalCycle, updated when user picks

  const plan = PLAN_PRICES[planKey] || PLAN_PRICES['pack_two'];

  const getBaseAmount = () => {
    if (currency === 'usd') {
      if (cycle === 'quarterly') return plan.usd_q;
      if (cycle === 'yearly') return plan.usd_y;
      return plan.usd_m;
    }
    if (cycle === 'quarterly') return plan.inr_q;
    if (cycle === 'yearly') return plan.inr_y;
    return plan.inr_m;
  };

  const getGST = () => {
    // GST 18% only when India tab selected AND INR currency
    if (tab === 'india' && currency === 'inr') return Math.round(getBaseAmount() * 0.18);
    return 0;
  };

  const getAmount = () => {
    const base = getBaseAmount();
    if (currency === 'usd') {
      if (cycle === 'quarterly') return `$${base}/3 months`;
      if (cycle === 'yearly') return `$${base}/year`;
      return `$${base}/month`;
    }
    if (cycle === 'quarterly') return `₹${base.toLocaleString('en-IN')}/3 months`;
    if (cycle === 'yearly') return `₹${base.toLocaleString('en-IN')}/year`;
    return `₹${base.toLocaleString('en-IN')}/month`;
  };

  const getTotalAmount = () => {
    const base = getBaseAmount();
    const gst = getGST();
    const total = base + gst;
    if (currency === 'usd') return `$${base}`;
    return `₹${total.toLocaleString('en-IN')}`;
  };

  const handleSubmit = async () => {
    if (!txnId.trim()) return toast.error('Please enter your Transaction ID / UTR Number');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('txn_id', txnId);
      fd.append('bank_account', tab);
      fd.append('currency', currency);
      fd.append('billing_cycle', cycle);
      fd.append('plan_key', planKey);
      fd.append('business_id', bizId);
      fd.append('email', email);
      fd.append('address', address);
      fd.append('gstin', gstin);
      if (file) fd.append('screenshot', file);

      await axios.post('/api/payment/submit-public', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Save renewal flag before clearing sessionStorage
      const wasRenewal = !!bizId;
      // Clear sessionStorage
      ['pay_email','pay_password','pay_company','pay_plan','pay_currency','pay_cycle','pay_business_id'].forEach(k => sessionStorage.removeItem(k));
      // Keep renewal flag for redirect
      if (wasRenewal) sessionStorage.setItem('pay_was_renewal', '1');

      toast.success('Payment submitted successfully!');
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // After submit for renewal - go to dashboard not login
  const handleAfterSubmit = () => {
    if (isRenewal) {
      window.location.href = '/dashboard/billing';
    }
  };

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(0,213,91,0.1)', border: '2px solid #00d55b', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 36 }}>✓</div>
        <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 12, color: '#00d55b' }}>Payment Submitted!</h2>
        <p style={{ color: '#888', fontSize: 15, lineHeight: 1.8, marginBottom: 12 }}>
          Thank you! Your payment is under review by our team.
        </p>
        <p style={{ color: '#888', fontSize: 14, lineHeight: 1.8, marginBottom: 24 }}>
          Once verified, your account will be {isRenewal ? 'renewed' : 'activated'} and you will receive a confirmation email at <strong style={{ color: '#fff' }}>{email}</strong>.
        </p>

        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20, marginBottom: 24, textAlign: 'left' }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>What happens next?</h4>
          {['Our admin verifies your payment (usually within a few hours)', 'Your account gets activated automatically', 'Login using your email and password', 'Set up your chat widget and go live!'].map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,213,91,0.15)', border: '1px solid rgba(0,213,91,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#00d55b', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
              <span style={{ color: '#888', fontSize: 13, lineHeight: 1.5 }}>{s}</span>
            </div>
          ))}
        </div>
        <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>
          Need help? <a href="mailto:halo@msdiscoveries.com" style={{ color: '#00d55b' }}>halo@msdiscoveries.com</a> · <a href="tel:+919443724724" style={{ color: '#00d55b' }}>+91 9443724724</a>
        </div>
        {isRenewal ? (
          <button onClick={() => { sessionStorage.removeItem('pay_was_renewal'); window.location.href = '/dashboard/billing'; }}
            style={{ display: 'inline-block', padding: '12px 32px', background: '#00d55b', color: '#000', borderRadius: 10, fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer' }}>
            Go to Dashboard →
          </button>
        ) : (
          <Link to="/login" style={{ display: 'inline-block', padding: '12px 32px', background: '#00d55b', color: '#000', borderRadius: 10, fontWeight: 700, fontSize: 15 }}>
            Go to Login →
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#000', padding: '20px' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', padding: '24px 0 28px' }}><Logo /></div>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8 }}>Complete Your Payment</h1>
          {company && <p style={{ color: '#888', fontSize: 14, marginBottom: 4 }}>Hi <strong style={{ color: '#fff' }}>{company}</strong>! You're almost there.</p>}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, background: '#111', border: '1px solid #1e1e1e', borderRadius: 10, padding: '10px 20px', marginTop: 8 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#555' }}>Plan</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#00d55b' }}>{plan.name}</div>
            </div>
            <div style={{ width: 1, height: 30, background: '#1e1e1e' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#555' }}>Amount</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{getAmount()}</div>
            </div>
            <div style={{ width: 1, height: 30, background: '#1e1e1e' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#555' }}>Now Pay</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#00d55b' }}>{getTotalAmount()}</div>
              {(currency === 'inr' || tab === 'india') && <div style={{ fontSize: 10, color: '#555' }}>incl. 18% GST (₹{getGST().toLocaleString('en-IN')})</div>}
            </div>
          </div>
        </div>

        {/* Steps */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: '#1e1e1e', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
          {[
            ['1', 'Transfer Payment', 'Make bank transfer to the account below'],
            ['2', 'Enter TXN ID', 'Submit your transaction ID as proof'],
            ['3', 'Get Activated', 'Admin verifies and activates your account']
          ].map(([n, title, desc]) => (
            <div key={n} style={{ background: '#0d0d0d', padding: '16px 14px', textAlign: 'center' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(0,213,91,0.15)', border: '1px solid rgba(0,213,91,0.3)', margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00d55b', fontSize: 13, fontWeight: 700 }}>{n}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 11, color: '#555' }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* Billing Cycle selector - always show */}
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12 }}>Select Billing Cycle:</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {allowedCycles.map(c => (
              <button key={c} onClick={() => setSelectedCycle(c)}
                style={{ flex: 1, padding: '12px 8px', borderRadius: 8, border: `2px solid ${selectedCycle === c ? '#00d55b' : '#1e1e1e'}`, background: selectedCycle === c ? 'rgba(0,213,91,0.1)' : '#0d0d0d', color: selectedCycle === c ? '#00d55b' : '#555', fontWeight: selectedCycle === c ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>
                <div>{c === 'monthly' ? 'Monthly' : c === 'quarterly' ? 'Quarterly' : 'Yearly'}</div>
                {c === 'quarterly' && <div style={{ fontSize: 10, color: '#00d55b', marginTop: 2 }}>Save 10%</div>}
                {c === 'yearly' && <div style={{ fontSize: 10, color: '#00d55b', marginTop: 2 }}>Save 20%</div>}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* Bank Details */}
          <div style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: currency === 'inr' ? '1fr' : '1fr 1fr', background: '#111', borderBottom: '1px solid #1e1e1e' }}>
              {(currency === 'inr' ? [['india', '🇮🇳 India (INR)']] : [['india', '🇮🇳 India (INR)'], ['us', '🇺🇸 US (USD)']]).map(([t, l]) => (
                <button key={t} onClick={() => setTab(t)}
                  style={{ padding: '13px', background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === t ? '#00d55b' : 'transparent'}`, color: tab === t ? '#00d55b' : '#555', fontSize: 13, fontWeight: tab === t ? 600 : 400, cursor: 'pointer' }}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 14, background: 'rgba(0,213,91,0.06)', border: '1px solid rgba(0,213,91,0.15)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#888' }}>Transfer exactly this amount</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#00d55b', marginTop: 2 }}>{getTotalAmount()}</div>
                {tab === 'india' && currency === 'inr' && (
                  <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>
                    Base: ₹{getBaseAmount().toLocaleString('en-IN')} + GST 18%: ₹{getGST().toLocaleString('en-IN')}
                  </div>
                )}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                {(tab === 'india' ? INDIA_BANK : US_BANK).map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid #111' }}>
                    <td style={{ color: '#555', fontSize: 12, padding: '8px 0' }}>{k}</td>
                    <td style={{ color: '#fff', fontSize: 12, fontFamily: 'monospace', textAlign: 'right', fontWeight: 500, padding: '8px 0' }}>
                      {v}
                      <button onClick={() => { navigator.clipboard.writeText(v); toast.success('Copied!'); }}
                        style={{ marginLeft: 6, background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 11 }}>
                        📋
                      </button>
                    </td>
                  </tr>
                ))}
              </table>
            </div>
          </div>

          {/* Submit Payment */}
          <div style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 16, padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Submit Payment Proof</h3>
            <p style={{ color: '#555', fontSize: 13, lineHeight: 1.6, marginTop: -8 }}>
              After making the bank transfer, enter your Transaction ID / UTR Number below as proof of payment.
            </p>

            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Transaction ID / UTR Number *</label>
              <input
                value={txnId}
                onChange={e => setTxnId(e.target.value)}
                placeholder="Enter TXN ID or UTR number from your bank"
                style={{ fontSize: 14 }}
              />
              <p style={{ color: '#444', fontSize: 11, marginTop: 5 }}>Found in your bank app under transaction details</p>
            </div>

            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Payment Screenshot <span style={{ color: '#555' }}>(optional but recommended)</span></label>
              <div
                onClick={() => document.getElementById('pay-screenshot').click()}
                style={{ border: `2px dashed ${file ? '#00d55b' : '#1e1e1e'}`, borderRadius: 10, padding: '20px 16px', textAlign: 'center', cursor: 'pointer', background: file ? 'rgba(0,213,91,0.04)' : '#111', transition: 'all 0.2s' }}>
                <input id="pay-screenshot" type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                {file ? (
                  <div>
                    <div style={{ color: '#00d55b', fontSize: 20, marginBottom: 4 }}>✓</div>
                    <div style={{ color: '#00d55b', fontSize: 13, fontWeight: 500 }}>{file.name}</div>
                    <div style={{ color: '#555', fontSize: 11, marginTop: 2 }}>Click to change</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ color: '#555', fontSize: 22, marginBottom: 6 }}>📎</div>
                    <div style={{ color: '#555', fontSize: 13 }}>Click to upload screenshot</div>
                    <div style={{ color: '#333', fontSize: 11, marginTop: 2 }}>PNG, JPG, PDF accepted</div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ background: '#111', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, color: '#888', lineHeight: 1.7 }}>
                <div>📧 Confirmation: <strong style={{ color: '#fff' }}>{email}</strong></div>
                <div style={{ marginTop: 4 }}>🏢 <strong style={{ color: '#fff' }}>{company}</strong></div>
                {address && <div style={{ marginTop: 4 }}>📍 {address}</div>}
                {gstin && <div style={{ marginTop: 4 }}>GST: <strong style={{ color: '#fff' }}>{gstin}</strong></div>}
                <div style={{ marginTop: 4 }}>⏱ Activation usually within <strong style={{ color: '#00d55b' }}>a few hours</strong></div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading || !txnId.trim()}
              style={{ padding: '14px', background: !txnId.trim() ? '#1a1a1a' : '#00d55b', color: !txnId.trim() ? '#555' : '#000', borderRadius: 10, fontWeight: 700, fontSize: 15, border: `1px solid ${!txnId.trim() ? '#1e1e1e' : '#00d55b'}`, cursor: txnId.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.2s', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Submitting...' : '✓ Submit Payment'}
            </button>

            <p style={{ fontSize: 12, color: '#333', textAlign: 'center' }}>
              Need help? <a href="tel:+919443724724" style={{ color: '#555' }}>+91 9443724724</a> · <a href="mailto:halo@msdiscoveries.com" style={{ color: '#555' }}>halo@msdiscoveries.com</a>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}